import { motion } from "framer-motion";
import { Search, Microscope, BarChart3, Users, CheckCircle, Lightbulb, Target, FileText, Award, Globe } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import EuropeMap from "@/components/EuropeMap";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "@/hooks/useTranslation";
const AnimatedStatCard = ({
  stat,
  index
}: {
  stat: any;
  index: number;
}) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  const startAnimation = () => {
    if (hasAnimated) return;
    setHasAnimated(true);
    
    let current = 0;
    const increment = stat.value / 50; // Slower, smoother animation
    const counter = setInterval(() => {
      current += increment;
      if (current >= stat.value) {
        setCount(stat.value);
        clearInterval(counter);
      } else {
        setCount(Math.floor(current));
      }
    }, 40); // Smoother animation
  };
  return <motion.div className="relative text-center p-4 sm:p-6 md:p-8 rounded-3xl bg-white border border-primary/10 shadow-sm hover:shadow-md animate-fade-in overflow-hidden" initial={{
    opacity: 0,
    y: 40,
    scale: 0.8
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.8,
    delay: 1.4 + index * 0.15,
    type: "spring",
    bounce: 0.3
  }} onViewportEnter={startAnimation} viewport={{
    once: true,
    margin: "-100px"
  }}>
      <div className="relative z-10">
        <motion.div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 py-[5px] bg-gradient-to-br from-current to-current/70 bg-clip-text" style={{
        color: stat.color
      }} initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 1.6 + index * 0.15,
        type: "spring",
        bounce: 0.6
      }}>
          {count}{stat.suffix}
        </motion.div>
        <motion.div className="text-xs sm:text-sm font-medium text-neutral-700 leading-relaxed py-[2px] px-2 break-words hyphens-auto" style={{ wordBreak: 'break-word' }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 1.8 + index * 0.15
      }}>
          {stat.label}
        </motion.div>
      </div>
    </motion.div>;
};
const ResearchOnDemand = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile || !videoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && videoRef.current) {
            videoRef.current.play().catch(() => {
              // Handle autoplay restrictions gracefully
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, [isMobile]);
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      <main className="pt-32 pb-16 relative overflow-hidden">
        
        <div className="container-padding relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Hero Section */}
            <motion.div className="text-center mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }}>
              <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 
                           border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium
                           backdrop-blur-sm mb-6" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
                <Search className="w-4 h-4" />
                {t.researchOnDemand.badge}
              </motion.div>
              
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.3
            }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-8 py-[10px] text-accent-teal font-bold">
                {t.researchOnDemand.title}
              </motion.h1>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.4
            }} className="text-xl sm:text-2xl md:text-3xl font-bold text-primary mb-6 py-[5px] px-4">
                {t.researchOnDemand.subtitle}
              </motion.h2>
              
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.5
            }} className="text-xl text-neutral-700 max-w-4xl mx-auto leading-relaxed py-[2px]">
                {t.researchOnDemand.description}
              </motion.p>
            </motion.div>


            {/* What We Offer Section */}
            <motion.div className="mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }}>
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent-teal bg-clip-text text-transparent mb-4 py-[10px]">
                  {t.researchOnDemand.whatWeOffer}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-accent-teal to-primary rounded-full mx-auto" />
              </div>
              
              <div className="grid gap-6">
              {[{
                icon: Target,
                color: "#A9E0DD"
              }, {
                icon: CheckCircle,
                color: "#66B3BB"
              }, {
                icon: Users,
                color: "#C8DDFF"
              }, {
                icon: BarChart3,
                color: "#868EAD"
              }, {
                icon: FileText,
                color: "#B4C3FD"
              }].map((service, index) => <motion.div key={index} className="group relative p-4 sm:p-6 rounded-2xl border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 overflow-hidden" style={{
                backgroundColor: service.color + '40'
              }} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.8 + index * 0.1
              }}>
                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  backgroundColor: service.color + '60'
                }} />
                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500" style={{
                  backgroundColor: service.color + '50'
                }} />
                    
                    <div className="relative z-10 flex items-start gap-3 sm:gap-5">
                      <div className="relative flex-shrink-0">
                        <div className="absolute inset-0 rounded-2xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
                      backgroundColor: service.color + '80'
                    }} />
                        <div className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg bg-white/20">
                          <service.icon className="w-6 h-6 sm:w-8 sm:h-8 text-slate-800 transition-all duration-300" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 py-[5px] text-slate-800 break-words">
                          {t.researchOnDemand.services[index].title}
                        </h3>
                        <p className="text-sm sm:text-base leading-relaxed py-[2px] text-slate-800 break-words hyphens-auto" style={{ wordBreak: 'break-word' }}>
                          {t.researchOnDemand.services[index].description}
                        </p>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Global Reach Video */}
            <motion.div className="mb-20 relative" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.2
          }}>
              {/* Background decorative elements */}
              <div className="absolute -top-10 -left-10 w-32 h-32 bg-gradient-to-br from-accent-teal/10 to-secondary/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-br from-primary/10 to-accent-teal/10 rounded-full blur-2xl" />
              
              <div className="relative z-10">
                <div className="text-center mb-16">
                  <motion.div className="inline-flex items-center gap-3 bg-gradient-to-r from-accent-teal/10 to-primary/10 border border-accent-teal/20 rounded-full px-8 py-4 mb-6" initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.6,
                  delay: 1.4
                }}>
                    <Globe className="w-5 h-5 text-accent-teal" />
                    <span className="text-primary font-semibold">{t.researchOnDemand.globalNetwork.badge}</span>
                  </motion.div>
                  
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary via-secondary to-accent-teal bg-clip-text text-transparent mb-6 py-[10px] px-4 break-words hyphens-auto leading-tight" style={{ wordBreak: 'break-word' }}>
                    {t.researchOnDemand.globalNetwork.title}
                  </h2>
                  
                  <p className="text-xl text-neutral-700 max-w-4xl mx-auto leading-relaxed py-[2px]">
                    {t.researchOnDemand.globalNetwork.description}
                  </p>
                </div>
                
                <div className="relative max-w-5xl mx-auto">
                  {/* Floating elements around video */}
                  <div className="absolute -top-6 -left-6 w-12 h-12 rounded-xl rotate-12 opacity-80 animate-pulse" style={{
                  backgroundColor: '#A9E0DD'
                }} />
                  <div className="absolute -top-4 -right-8 w-8 h-8 rounded-full opacity-70 animate-bounce" style={{
                  backgroundColor: '#66B3BB'
                }} />
                  <div className="absolute -bottom-8 -left-4 w-10 h-10 rounded-lg -rotate-12 opacity-60 animate-pulse" style={{
                  backgroundColor: '#C8DDFF'
                }} />
                  <div className="absolute -bottom-6 -right-6 w-14 h-14 rounded-2xl rotate-45 animate-pulse" style={{
                  backgroundColor: '#B4C3FD'
                }} />
                  <div className="absolute top-1/3 -left-8 w-6 h-6 rounded-full opacity-50 animate-pulse" style={{
                  backgroundColor: '#868EAD'
                }} />
                  <div className="absolute bottom-1/3 -right-10 w-9 h-9 rounded-lg rotate-12 opacity-60 animate-bounce" style={{
                  backgroundColor: '#70989c'
                }} />
                  
                  {/* Video container with enhanced design */}
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-accent-teal via-primary to-secondary rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
                    <div className="relative bg-white rounded-3xl p-3 shadow-2xl">
                      <div className="relative overflow-hidden rounded-2xl">
                        <div className="absolute inset-0 bg-gradient-to-br from-accent-teal/5 to-primary/5 z-10 pointer-events-none" />
                        <video ref={videoRef} className="w-full h-auto rounded-2xl" controls preload="metadata" poster="" loop muted>
                          <source src="/videos/myVisitedPlaces_3.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      
                    </div>
                  </div>
                  
                  {/* Stats around video */}
                </div>
              </div>
            </motion.div>

            {/* Our Track Record */}
            <motion.div className="healthcare-card group mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.0
          }}>
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent-teal/5 rounded-2xl" />
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-4 mb-8">
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-secondary to-accent-teal text-white">
                    <Award className="w-10 h-10" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary py-[10px] px-4 break-words hyphens-auto leading-tight" style={{ wordBreak: 'break-word' }}>{t.researchOnDemand.trackRecord.title}</h2>
                </div>
                <p className="text-xl text-neutral-700 leading-relaxed max-w-4xl mx-auto py-[2px] text-center">
                  {t.researchOnDemand.trackRecord.description}
                </p>
              </div>
            </motion.div>

            {/* Statistics */}
            <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.4
          }}>
              {[{
              value: 25,
              suffix: "+",
              color: "hsl(var(--primary))"
            }, {
              value: 50,
              suffix: "+",
              color: "hsl(var(--secondary))"
            }, {
              value: 30,
              suffix: "+",
              color: "hsl(var(--accent-teal))"
            }, {
              value: 21,
              suffix: "",
              color: "hsl(var(--primary))"
            }].map((stat, index) => <AnimatedStatCard key={index} stat={{...stat, label: t.researchOnDemand.stats[index].label}} index={index} />)}
            </motion.div>

            
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default ResearchOnDemand;